import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./user-management.module.css";
import PageTitle from "../../components/screens/page-title";
import Table from "../../components/ui/table";
import Input from "../../components/ui/input";
import Button from "../../components/ui/button";
import Dropdown from "../../components/ui/dropdown";
import Modal from "../../components/ui/modal";
import { register, getWorkers, getUsersAdmin, changePasswordAdmin } from "../../services/apiService";
import { showSuccessToast, showErrorToast, notifications } from "../../utils/notifications";
import { MyIcons } from "../../components/icons/my-icons";
import usePageTitle from "../../hooks/usePageTitle";

const UserManagementPage = () => {
  usePageTitle('Gestión de Usuarios');
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [operarios, setOperarios] = useState([]);
  const [operarioOptions, setOperarioOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [userToChangePassword, setUserToChangePassword] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [contextMenuOpen, setContextMenuOpen] = useState(null);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });

  // Función para recargar usuarios
  const reloadUsers = async (page = currentPage) => {
    setIsLoading(true);
    try {
      const usersData = await getUsersAdmin(page, 20);
      console.log("Users data:", usersData);
      setUsers(usersData.content || []);
      setTotalPages(usersData.totalPages || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const headers = [
    { label: "Usuario", key: "username" },
    { label: "Nombre", key: "nombre" },
    { 
      label: "Rol", 
      key: "rol",
      render: (value) => {
        const roleOption = roleOptions.find(option => option.value === value);
        return roleOption ? roleOption.label : value;
      }
    },
    { 
      label: "Grupos de Trabajo", 
      key: "gruposTrabajo"
    },
    { 
      label: "Activo", 
      key: "activo"
    }
  ];

  const handleToggleContextMenu = (e, row) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenuPosition({
      x: rect.left,
      y: rect.bottom + 5
    });
    setContextMenuOpen(contextMenuOpen === row.usuarioId ? null : row.usuarioId);
  };

  const handleCloseContextMenu = () => {
    setContextMenuOpen(null);
  };

  const renderActions = (row) => {
    const isMenuOpen = contextMenuOpen === row.usuarioId;
    
    return (
      <div style={{ display: "flex", justifyContent: "center", width: "100%", position: "relative" }}>
        <button
          onClick={(e) => handleToggleContextMenu(e, row)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Opciones"
        >
          <MyIcons name="threeDots" width={20} height={20} color="var(--dark-grey)" />
        </button>
        
        {isMenuOpen && (
          <>
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
              }}
              onClick={handleCloseContextMenu}
            />
            <div
              style={{
                position: "fixed",
                top: `${contextMenuPosition.y}px`,
                left: `${contextMenuPosition.x}px`,
                backgroundColor: "white",
                border: "1px solid #ddd",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                zIndex: 1000,
                minWidth: "180px",
                overflow: "hidden",
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseContextMenu();
                  handleEditUser(row);
                }}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  border: "none",
                  background: "none",
                  textAlign: "left",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "black",
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f5f5f5"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                Editar usuario
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseContextMenu();
                  handleOpenPasswordModal(row);
                }}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  border: "none",
                  background: "none",
                  textAlign: "left",
                  cursor: "pointer",
                  fontSize: "14px",
                  color: "black",
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f5f5f5"}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
              >
                Cambiar contraseña
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  const roleOptions = [
    { value: "ROLE_USER", label: "Usuario" },
    { value: "ROLE_MANAGER", label: "Manager" },
    { value: "ROLE_ADMIN", label: "Administrador" },
    { value: "ROLE_OPERARIO", label: "Operario" },
  ];

  const grupoTrabajoOptions = [
    { value: "Grupo A", label: "Grupo A" },
    { value: "Grupo B", label: "Grupo B" },
  ];

  // useEffect para cargar usuarios y operarios
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Cargar usuarios - 20 por página con paginación
        await reloadUsers(currentPage);
        
        // Cargar operarios solo la primera vez
        if (operarios.length === 0) {
          const operariosData = await getWorkers();
          const operariosList = operariosData.map(operario => ({
            value: operario.id,
            label: operario.nombre
          }));
          setOperarios(operariosList);
          setOperarioOptions(operariosList);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [currentPage]);

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const filteredUsers = users.filter(
    (user) =>
      user.username.toLowerCase().includes(searchValue.toLowerCase()) ||
      user.nombre.toLowerCase().includes(searchValue.toLowerCase()) ||
      user.rol.toLowerCase().includes(searchValue.toLowerCase()) ||
      (Array.isArray(user.gruposTrabajo) && user.gruposTrabajo.some(grupo => 
        grupo.toLowerCase().includes(searchValue.toLowerCase())
      ))
  );

  // Transformar los datos para mostrar "Activo" como texto y grupos con comas
  const transformedUsers = filteredUsers.map(user => ({
    ...user,
    activo: user.activo ? "Sí" : "No",
    gruposTrabajo: Array.isArray(user.gruposTrabajo) && user.gruposTrabajo.length > 0 
      ? user.gruposTrabajo.join(", ") 
      : user.operarioNombre || "No asignado"
  }));

  const handleEditUser = (user) => {
    // Buscar el usuario original sin transformar para pasarlo al edit
    const originalUser = filteredUsers.find(u => u.usuarioId === user.usuarioId);
    console.log("Usuario original encontrado:", originalUser);
    console.log("Grupos de trabajo del usuario:", originalUser?.gruposTrabajo);
    // Navigate to user edit page con el usuario original que tiene gruposTrabajo como array
    navigate(`/user-edit/${user.usuarioId}`, { 
      state: { 
        user: originalUser || user  // Fallback a user si no se encuentra
      } 
    });
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "La contraseña debe tener al menos 8 caracteres";
    }
    if (!/[A-Z]/.test(password)) {
      return "La contraseña debe contener al menos una letra mayúscula";
    }
    return "";
  };

  const handleCreateUser = () => {
    setCurrentUser({
      id: null,
      username: "",
      name: "",
      password: "",
      role: null,
      gruposTrabajo: [], // Changed from grupoTrabajo to gruposTrabajo array
      operarioVinculado: null,
    });
    setConfirmPassword("");
    setPasswordError("");
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleOpenPasswordModal = (user) => {
    setUserToChangePassword(user);
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordError("");
    setIsPasswordModalOpen(true);
  };

  const handleChangePasswordSubmit = async () => {
    // Validar contraseñas
    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }

    try {
      await changePasswordAdmin(userToChangePassword.usuarioId, {
        newPassword: newPassword
      });
      
      showSuccessToast("Contraseña cambiada exitosamente");
      setIsPasswordModalOpen(false);
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordError("");
      setUserToChangePassword(null);
    } catch (error) {
      
      // Recargar la lista de usuarios
      await reloadUsers();
      console.error("Error changing password:", error);
      showErrorToast("Error al cambiar la contraseña");
    }
  };

  const handleSaveUser = async () => {
    try {
      if (currentUser.id) {
        // Editar usuario existente - convertir rol a string
        const updatedUser = {
          ...currentUser,
          role: currentUser.role.value || currentUser.role
        };
        setUsers(
          users.map((user) => (user.id === currentUser.id ? updatedUser : user))
        );
        showSuccessToast(notifications.update.success("Usuario"));
      } else {
        // Validar contraseña al crear nuevo usuario
        const validationError = validatePassword(currentUser.password);
        if (validationError) {
          setPasswordError(validationError);
          return;
        }

        if (currentUser.password !== confirmPassword) {
          setPasswordError("Las contraseñas no coinciden");
          return;
        }

        // Crear nuevo usuario usando la API
        const roleValue = currentUser.role.value || currentUser.role;
        const userData = {
          username: currentUser.username,
          password: currentUser.password,
          rol: roleValue,
          nombre: currentUser.name,
          operarioId: null,
          gruposTrabajo: [],
          activo: true
        };
        
        // Agregar campos específicos según el rol
        if (roleValue === "ROLE_OPERARIO") {
          userData.operarioId = currentUser.operarioVinculado || null;
          userData.gruposTrabajo = [];
        } else if (roleValue === "ROLE_USER" || roleValue === "ROLE_MANAGER") {
          userData.operarioId = null;
          userData.gruposTrabajo = currentUser.gruposTrabajo || [];
        }
        
        await register(userData);
        
        // Refrescar la lista de usuarios después de crear - volver a página 0
        if (currentPage === 0) {
          // Si ya estamos en página 0, forzar recarga
          await reloadUsers(0);
        } else {
          // Si estamos en otra página, cambiar a 0 (el useEffect recargará)
          setCurrentPage(0);
        }
        
        showSuccessToast(notifications.create.success("Usuario"));
      }
      setIsModalOpen(false);
      setSelectedUser(null);
      setConfirmPassword("");
      setPasswordError("");
    } catch (error) {
      console.error("Error saving user:", error);
      showErrorToast(error.response?.data?.message || 
        (currentUser.id ? 
          notifications.update.error("el usuario") : 
          notifications.create.error("el usuario")
        )
      );
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentUser({ ...currentUser, [name]: value });
  };

  const handleDropdownSelect = (field, option) => {
    if (field === "role") {
      // Para roles, option es una sola opción
      setCurrentUser({ ...currentUser, [field]: option });
    } else if (field === "gruposTrabajo") {
      // Para grupos de trabajo, option es un array de opciones seleccionadas
      const grupoValues = Array.isArray(option) ? option.map(opt => opt.value) : [];
      setCurrentUser({ ...currentUser, [field]: grupoValues });
    } else {
      // Para otros campos, option es una sola opción
      setCurrentUser({ ...currentUser, [field]: option.value });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
      <PageTitle iconName="user" title="Gestión de Usuarios" />

      <div className={styles.searchContainer}>
        <Input
          name="search"
          value={searchValue}
          onChange={handleSearch}
          placeholder="Buscar"
          isSearch={true}
        />
        <Button onClick={handleCreateUser}>NUEVO USUARIO</Button>
      </div>

      <Table
        headers={headers}
        data={transformedUsers}
        renderActions={renderActions}
      />

      {/* Controles de paginación */}
      {totalPages > 1 && (
        <div className={styles.paginationContainer}>
          <Button
            variant="secondary"
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
          >
            Anterior
          </Button>
          <span className={styles.pageInfo}>
            Página {currentPage + 1} de {totalPages}
          </span>
          <Button
            variant="secondary"
            onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
            disabled={currentPage === totalPages - 1}
          >
            Siguiente
          </Button>
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        title={selectedUser ? "Editar Usuario" : "Crear Usuario"}
      >
        <form
          className={styles.userForm}
          onSubmit={(e) => {
            e.preventDefault();
            handleSaveUser();
          }}
        >
          <Input
            label="Nombre de Usuario"
            name="username"
            style={{ width: "100%" }}
            value={currentUser?.username || ""}
            onChange={handleInputChange}
            placeholder="Nombre de usuario"
          />

          <Input
            label="Nombre Completo"
            name="name"
            style={{ width: "100%" }}
            value={currentUser?.name || ""}
            onChange={handleInputChange}
            placeholder="Nombre completo"
          />

          {!selectedUser && (
            <>
              <Input
                label="Contraseña"
                name="password"
                type="password"
                style={{ width: "100%" }}
                value={currentUser?.password || ""}
                onChange={handleInputChange}
                placeholder="Mínimo 8 caracteres, 1 mayúscula"
              />

              <Input
                label="Confirmar Contraseña"
                name="confirmPassword"
                type="password"
                style={{ width: "100%" }}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setPasswordError("");
                }}
                placeholder="Repetir contraseña"
              />

              {passwordError && (
                <p style={{ color: "red", fontSize: "14px", margin: "0" }}>
                  {passwordError}
                </p>
              )}
            </>
          )}

          <Dropdown
            label="Rol"
            options={roleOptions}
            onSelect={(option) => handleDropdownSelect("role", option)}
            selected={roleOptions.find(option => option.value === (currentUser?.role?.value || currentUser?.role))}
            placeholder="Seleccionar rol"
          />

          {/* Mostrar grupos de trabajo solo para USER y MANAGER */}
          {((currentUser?.role?.value === "ROLE_USER" || currentUser?.role === "ROLE_USER") ||
            (currentUser?.role?.value === "ROLE_MANAGER" || currentUser?.role === "ROLE_MANAGER")) && (
            <Dropdown
              label="Grupos de Trabajo"
              options={grupoTrabajoOptions}
              onSelect={(option) => handleDropdownSelect("gruposTrabajo", option)}
              selected={grupoTrabajoOptions.filter(opt => 
                currentUser?.gruposTrabajo?.includes(opt.value)
              )}
              placeholder="Seleccionar grupos de trabajo"
              multiSelect={true}
            />
          )}

          {/* Mostrar operarios solo para OPERARIO */}
          {(currentUser?.role?.value === "ROLE_OPERARIO" || currentUser?.role === "ROLE_OPERARIO") && (
            <Dropdown
              label="Operario Vinculado"
              options={operarioOptions}
              onSelect={(option) => handleDropdownSelect("operarioVinculado", option)}
              selected={operarioOptions.find(
                (option) => option.value === currentUser?.operarioVinculado
              )}
              placeholder="Seleccionar operario"
            />
          )}

          <div className={styles.modalButtons}>
            {!selectedUser && (
              <Button type="submit">CREAR</Button>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setSelectedUser(null);
                setConfirmPassword("");
                setPasswordError("");
              }}
            >
              CANCELAR
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal para cambiar contraseña */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={() => {
          setIsPasswordModalOpen(false);
          setUserToChangePassword(null);
          setNewPassword("");
          setConfirmNewPassword("");
          setPasswordError("");
        }}
        title={`Cambiar Contraseña - ${userToChangePassword?.username || ""}`}
      >
        <form
          className={styles.userForm}
          onSubmit={(e) => {
            e.preventDefault();
            handleChangePasswordSubmit();
          }}
        >
          <Input
            label="Nueva Contraseña"
            name="newPassword"
            type="password"
            style={{ width: "100%" }}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setPasswordError("");
            }}
            placeholder="Mínimo 8 caracteres, 1 mayúscula"
          />

          <Input
            label="Confirmar Nueva Contraseña"
            name="confirmNewPassword"
            type="password"
            style={{ width: "100%" }}
            value={confirmNewPassword}
            onChange={(e) => {
              setConfirmNewPassword(e.target.value);
              setPasswordError("");
            }}
            placeholder="Repetir nueva contraseña"
          />

          {passwordError && (
            <p style={{ color: "red", fontSize: "14px", margin: "0" }}>
              {passwordError}
            </p>
          )}

          <div className={styles.modalButtons}>
            <Button type="submit">CAMBIAR CONTRASEÑA</Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsPasswordModalOpen(false);
                setUserToChangePassword(null);
                setNewPassword("");
                setConfirmNewPassword("");
                setPasswordError("");
              }}
            >
              CANCELAR
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserManagementPage;
